import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@1trading/react';
import { ArrowLeft, CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/feedback/empty-state';
import { LabeledValue } from '@/components/ui/labeled-value';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { BROKER_TYPE_CHIP_VARIANTS, CHIP_XS_CLASS } from '@/features/brokers/detail/broker-detail-format';
import { ClientsTab } from '@/features/brokers/detail/clients-tab';
import { ErrorPanel } from '@/features/brokers/detail/detail-panels';
import { FeeBandsTab } from '@/features/brokers/detail/fee-bands-tab';
import { FeeSettlementsTab } from '@/features/brokers/detail/fee-settlements-tab';
import { OverviewTab } from '@/features/brokers/detail/overview-tab';
import { TradesTab } from '@/features/brokers/detail/trades-tab';
import { isTxnSegment, TransactionsTab, type TxnSegment } from '@/features/brokers/detail/transactions-tab';
import { BROKER_TYPE_LABELS } from '@/features/brokers/list/broker-options';
import { isNotFoundError } from '@/hooks/queries/brokers/use-broker-batch';
import { useBrokerDetail } from '@/hooks/queries/brokers/use-broker-detail';
import { useDocumentTitle } from '@/hooks/use-document-title';

const BROKERS_LIST_ROUTE = '/operations/brokers';

const TAB_VALUES = ['overview', 'fee-bands', 'fee-settlements', 'clients', 'trades', 'transactions'] as const;
type BrokerTab = (typeof TAB_VALUES)[number];

const DEFAULT_TAB: BrokerTab = 'overview';
const DEFAULT_TXN_SEGMENT: TxnSegment = 'deposits';

const isBrokerTab = (value: string | null): value is BrokerTab => value !== null && (TAB_VALUES as readonly string[]).includes(value);

// Deliberate fix: legacy accepted ANY ?tab= string,
// producing an empty tab body for garbage values — validate against the known
// tabs and fall back to overview.
const parseTabParam = (value: string | null): BrokerTab => (isBrokerTab(value) ? value : DEFAULT_TAB);

const parseTxnParam = (value: string | null): TxnSegment => (isTxnSegment(value) ? value : DEFAULT_TXN_SEGMENT);

const BrokerDetailPage = () => {
  const navigate = useNavigate();
  // brokerId is the broker's account holder UUID (`s` from the list page)
  const { brokerId = '' } = useParams<{ brokerId: string }>();
  const [searchParams] = useSearchParams();

  // Deep links are read ONCE on mount — lazy initializers
  // capture the first render's params; later URL churn is ignored (legacy parity).
  // Starting on a deep-linked tab mounts that tab's content, which fires its lazy
  // load exactly like a manual activation would.
  const [activeTab, setActiveTab] = useState<BrokerTab>(() => parseTabParam(searchParams.get('tab')));
  // The segment lives up here so it survives tab switches; only the batch page's
  // back-link (?tab=transactions&txn=batches) produces the txn param.
  const [txnSegment, setTxnSegment] = useState<TxnSegment>(() => parseTxnParam(searchParams.get('txn')));

  const brokerQuery = useBrokerDetail(brokerId);
  const broker = brokerQuery.data;

  // Legacy useHead({ title: data.name }) — document title is the broker name
  useDocumentTitle(broker?.name);

  // Deliberate fix: legacy flattened every failure into the
  // not-found panel — only a definitive 404 ('BROKER_NOT_FOUND') gets it; anything
  // else keeps a retryable error state so transient failures don't look like
  // deleted brokers.
  const isNotFound = brokerQuery.isError && isNotFoundError(brokerQuery.error);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to={BROKERS_LIST_ROUTE}>
        <ArrowLeft className="size-4" />
        Back to brokers
      </Link>

      {brokerQuery.isPending ? (
        <LoadingPanel className="py-24" />
      ) : isNotFound ? (
        <EmptyState
          action={
            <Button onClick={() => navigate(BROKERS_LIST_ROUTE)} type="button" variant="outline">
              Back to Brokers
            </Button>
          }
          icon={CircleAlert}
          title="Broker not found."
        />
      ) : brokerQuery.isError ? (
        <ErrorPanel error={brokerQuery.error} onRetry={() => brokerQuery.refetch()} title="Failed to fetch broker details" />
      ) : broker ? (
        <>
          <header className="flex flex-col gap-3">
            <h1 className="text-2xl font-semibold">{broker.name}</h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <StatusChip className={CHIP_XS_CLASS} variant={BROKER_TYPE_CHIP_VARIANTS[broker.broker_type]}>
                {BROKER_TYPE_LABELS[broker.broker_type]}
              </StatusChip>
              <LabeledValue label="FAST ID" mono>
                {broker.fast_id}
              </LabeledValue>
              {/* `s` is the account holder UUID; fast_id is the ENCODED id — they never
                  visually match trading-stats' decoded broker_number, don't reconcile */}
              <LabeledValue label="Account ID" mono>
                {broker.s}
              </LabeledValue>
            </div>
          </header>

          {/* Radix unmounts inactive tab content: each tab lazy-loads on first
              activation, and the query cache keeps revisited tabs warm — the legacy
              refetch-empty-tabs quirk is gone by construction. */}
          <Tabs onValueChange={(value) => setActiveTab(parseTabParam(value))} value={activeTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="fee-bands">Fee Bands</TabsTrigger>
              <TabsTrigger value="fee-settlements">Fee Settlements</TabsTrigger>
              <TabsTrigger value="clients">Clients</TabsTrigger>
              <TabsTrigger value="trades">Trades</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
            </TabsList>

            <TabsContent className="mt-4" value="overview">
              <OverviewTab broker={broker} />
            </TabsContent>
            <TabsContent className="mt-4" value="fee-bands">
              <FeeBandsTab brokerId={brokerId} />
            </TabsContent>
            <TabsContent className="mt-4" value="fee-settlements">
              <FeeSettlementsTab brokerId={brokerId} />
            </TabsContent>
            <TabsContent className="mt-4" value="clients">
              <ClientsTab brokerId={brokerId} />
            </TabsContent>
            <TabsContent className="mt-4" value="trades">
              <TradesTab brokerId={brokerId} />
            </TabsContent>
            <TabsContent className="mt-4" value="transactions">
              <TransactionsTab brokerId={brokerId} onSegmentChange={setTxnSegment} segment={txnSegment} />
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </div>
  );
};
BrokerDetailPage.displayName = 'BrokerDetailPage';

export default BrokerDetailPage;
